/*
 * Previous/next controls for the desktop product gallery.
 *
 * Two jobs:
 *
 * 1. Stepping. In Dawn's `thumbnail` and `thumbnail_slider` layouts the desktop
 *    gallery is not a scrolling slider — only the `.is-active` media item is
 *    rendered and the thumbnails swap it via media-gallery's setActiveMedia().
 *    Dawn's built-in slider buttons scroll, so they do nothing in that layout;
 *    these arrows step the active media instead.
 *
 * 2. Placement. The media column is wider than the image whenever the image is
 *    portrait or is height-capped by `constrain_to_viewport`, so arrows pinned to
 *    the column would float in the empty gutter. The arrow strip is measured onto
 *    the rendered image box instead, which CSS alone cannot do — the image width
 *    is intrinsic.
 *
 * Clicks are delegated from the document so the controls survive the section
 * re-render that follows a variant change.
 */
(() => {
  const VIEWER = '[id^="GalleryViewer"]';

  const orderedMediaIds = (gallery) => {
    const thumbnails = gallery.querySelector('[id^="GalleryThumbnails"]');
    if (!thumbnails) return [];

    // The thumbnail list is the source of truth for order and for which media are
    // selectable — Dawn hides duplicate variant thumbnails via CSS.
    return Array.from(thumbnails.querySelectorAll('[data-target]'))
      .filter((item) => item.offsetParent !== null)
      .map((item) => item.dataset.target);
  };

  const alignArrows = (gallery) => {
    const arrows = gallery.querySelector('.product__media-arrows');
    const viewer = gallery.querySelector(VIEWER);
    if (!arrows || !viewer) return;

    const active = viewer.querySelector('[data-media-id].is-active') || viewer.querySelector('[data-media-id]');
    const media = active?.querySelector('img, iframe, video, model-viewer') || active;
    if (!media) return;

    const viewerRect = viewer.getBoundingClientRect();
    const mediaRect = media.getBoundingClientRect();
    if (!mediaRect.width || !mediaRect.height) return;

    arrows.style.left = `${mediaRect.left - viewerRect.left}px`;
    arrows.style.top = `${mediaRect.top - viewerRect.top}px`;
    arrows.style.width = `${mediaRect.width}px`;
    arrows.style.height = `${mediaRect.height}px`;
  };

  const galleries = () =>
    Array.from(document.querySelectorAll('.product__media-arrows'))
      .map((arrows) => arrows.closest('media-gallery'))
      .filter(Boolean);

  const alignAll = () => galleries().forEach(alignArrows);

  // Coalesce bursts of DOM churn into one measurement per frame.
  let scheduled = false;
  const scheduleObserve = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      observe();
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-gallery-step]');
    if (!button) return;

    const gallery = button.closest('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

    const mediaIds = orderedMediaIds(gallery);
    if (mediaIds.length < 2) return;

    const active = gallery.querySelector(`${VIEWER} [data-media-id].is-active`);
    const current = active ? mediaIds.indexOf(active.dataset.mediaId) : 0;
    const step = Number(button.dataset.galleryStep) || 0;

    // Wrap around, so neither arrow is ever a dead end.
    const next = (current + step + mediaIds.length) % mediaIds.length;
    gallery.setActiveMedia(mediaIds[next], false);
    requestAnimationFrame(() => alignArrows(gallery));
  });

  // A thumbnail click swaps the image too, and images of differing aspect ratio
  // change the box the arrows must sit on.
  document.addEventListener('click', (event) => {
    const thumbnail = event.target.closest('[data-target]');
    if (!thumbnail) return;
    const gallery = thumbnail.closest('media-gallery');
    if (gallery) requestAnimationFrame(() => alignArrows(gallery));
  });

  const observe = () => {
    galleries().forEach((gallery) => {
      const viewer = gallery.querySelector(VIEWER);
      if (!viewer || viewer.dataset.arrowsObserved) return;
      viewer.dataset.arrowsObserved = 'true';

      // Catches the initial image load, lazy loads, and any reflow of the column.
      new ResizeObserver(() => alignArrows(gallery)).observe(viewer);
      viewer.querySelectorAll('img').forEach((image) => {
        if (image.complete) return;
        image.addEventListener('load', () => alignArrows(gallery), { once: true });
      });
    });
    alignAll();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }

  window.addEventListener('load', observe);
  window.addEventListener('resize', alignAll);

  // Variant changes re-render the section; pick the new gallery up when it lands.
  document.addEventListener('shopify:section:load', scheduleObserve);
  new MutationObserver(scheduleObserve).observe(document.body, { childList: true, subtree: true });
})();
